import { Box } from '@mui/material';
import { memo, useMemo } from 'react';

interface PixelAvatarProps {
  address: string;
  size?: number;
}

/**
 * Classic Ethereum blockies PRNG (same family as MEW / Etherscan identicons).
 * @see https://github.com/ethereum/blockies
 */
function createRand(seed: string) {
  const randseed = [0, 0, 0, 0];
  for (let i = 0; i < seed.length; i += 1) {
    randseed[i % 4] = (randseed[i % 4] << 5) - randseed[i % 4] + seed.charCodeAt(i);
  }

  return () => {
    const t = randseed[0] ^ (randseed[0] << 11);
    randseed[0] = randseed[1];
    randseed[1] = randseed[2];
    randseed[2] = randseed[3];
    randseed[3] = randseed[3] ^ (randseed[3] >> 19) ^ t ^ (t >> 8);
    return (randseed[3] >>> 0) / (0x8000_0000 >>> 0);
  };
}

function createColor(rand: () => number) {
  const h = Math.floor(rand() * 360);
  const s = rand() * 60 + 40;
  // stacked lightness rolls → more mid tones, like original blockies
  const l = (rand() + rand() + rand() + rand()) * 25;
  return `hsl(${h} ${s}% ${l}%)`;
}

const GRID = 8;

/**
 * Classic Ethereum blockie — 8×8 mirrored pixels, 3-color palette.
 * Same address always produces the same icon.
 */
export const PixelAvatar = memo(function PixelAvatar({ address, size = 36 }: PixelAvatarProps) {
  const { bgcolor, color, spotcolor, data } = useMemo(() => {
    const seed = (address || '0x0').trim().toLowerCase();
    const rand = createRand(seed);
    const bg = createColor(rand);
    const fg = createColor(rand);
    const spot = createColor(rand);

    // Fill left half, then mirror horizontally (pareidolia / face-like symmetry)
    const width = Math.ceil(GRID / 2);
    const cells: number[] = [];
    for (let i = 0; i < GRID * width; i += 1) {
      cells.push(Math.floor(rand() * 2.3)); // ~0 bg, ~1 color, ~2 spot
    }

    const dataGrid: number[] = [];
    for (let y = 0; y < GRID; y += 1) {
      const row: number[] = [];
      for (let x = 0; x < width; x += 1) {
        row[x] = cells[y * width + x] ?? 0;
      }
      for (let x = GRID - 1; x >= width; x -= 1) {
        row[x] = row[GRID - 1 - x] ?? 0;
      }
      dataGrid.push(...row);
    }

    return { bgcolor: bg, color: fg, spotcolor: spot, data: dataGrid };
  }, [address]);

  const palette = [bgcolor, color, spotcolor] as const;
  const cell = size / GRID;

  return (
    <Box
      component="svg"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
      height={size}
      aria-hidden
      sx={{
        borderRadius: '20%',
        flexShrink: 0,
        display: 'block',
        overflow: 'hidden',
        shapeRendering: 'crispEdges',
        imageRendering: 'pixelated',
        boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)',
      }}
    >
      <rect width={size} height={size} fill={bgcolor} />
      {data.map((paletteIndex, i) => {
        if (paletteIndex === 0) return null;
        const x = i % GRID;
        const y = Math.floor(i / GRID);
        return (
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed 8×8 grid, index IS the cell identity
            key={i}
            x={x * cell}
            y={y * cell}
            width={cell + 0.5}
            height={cell + 0.5}
            fill={palette[paletteIndex] ?? color}
          />
        );
      })}
    </Box>
  );
});
